import { useState, useEffect } from "react";
import DialogBox from "../Common/DialogBox";
import CandidateNotes from "../../pages/PreScreen/CandidateNotes";
import DialogBoxSkeleton from "../Common/DialogBoxSkeleton";
import candidateService from "../../services/candidateService";
import InterviewDetailsForm from "./InterviewDetailsForm";

const CandidateHistory = ({ candidate }) => {
  const [showNotesPopup, setShowNotesPopup] = useState(false);
  const [showSlotsPopup, setShowSlotsPopup] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [applications, setApplications] = useState(
    candidate?.data?.applications || [],
  );

  useEffect(() => {
    setApplications(candidate?.data?.applications || []);
  }, [candidate?.data?.applications]);

  const fetchApplications = async () => {
    const candidateId =
      candidate?.data?.candidate?.id || candidate?.data?.candidate_id;
    if (!candidateId) return;
    try {
      const data = await candidateService.getCandidateById(candidateId);
      setApplications(data?.data?.applications || []);
    } catch (error) {
      console.error("Failed to fetch applications:", error);
    }
  };

  const handleViewNotes = () => {
    setShowNotesPopup(true);
  };

  const handleCloseNotes = () => {
    setShowNotesPopup(false);
  };

  const handleViewSlots = (job) => {
    setSelectedJob(job);
    setShowSlotsPopup(true);
  };

  const handleCloseSlots = () => {
    setSelectedJob(null);
    setShowSlotsPopup(false);
  };

  return (
    <div className="candidate-history">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold">Previous Applications</h3>
        <button
          onClick={handleViewNotes}
          className="bg-primary-dark text-white px-4 py-2 rounded-md hover:bg-primary-dark transition-colors"
        >
          View Notes
        </button>
      </div>

      {applications?.length > 0 ? (
        <div className="overflow-x-auto bg-card rounded-lg shadow-sm border border-border">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Vacancy Title
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Applied On
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {applications.map((job) => (
                <tr
                  key={job.vacancy?.title || job.id}
                  className="hover:bg-muted transition-colors duration-200"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground font-medium">
                    {job.vacancy?.title || "N/A"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    {job.vacancy?.campaign?.company?.name || "N/A"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    {job.created_at || "N/A"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1 items-start">
                      <span
                        className="text-xs font-semibold px-2 py-0.5 rounded-full border"
                        style={{
                          borderColor: `${job?.vacancy_status?.bg_color ?? "#EAECF0"}`,
                          color: `${job?.vacancy_status?.bg_color ?? "#667085"}`,
                          backgroundColor: `${(job?.vacancy_status?.bg_color ?? "#EAECF0") + "10"}`,
                        }}
                      >
                        {job.vacancy_status?.name || "N/A"}
                      </span>
                      {/* Book Interview - hidden for now
                      {job.company_availability_blocks?.some(
                        (block) => block.slots?.length > 0,
                      ) && (
                        <button
                          onClick={() => handleViewSlots(job)}
                          className="text-primary-dark hover:underline text-[11px] font-medium transition-all"
                        >
                          {job.booked_interview_slot
                            ? "Modify Interview"
                            : "Book Interview"}
                        </button>
                      )}
                      */}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <a
                      href={`/vacancies/${job.vacancy_id}/applications/${job.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-foreground hover:text-primary-dark font-semibold text-sm transition-colors"
                    >
                      View
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-muted-foreground">No jobs applied</p>
      )}

      {/* Notes Dialog */}
      <DialogBox
        isOpen={showNotesPopup}
        onClose={handleCloseNotes}
        title={
          <span className="text-xl font-semibold pl-4 text-foreground">
            Application Notes
          </span>
        }
        showIcon={false}
        className="w-[60%] max-md:w-[95%]"
      >
        <div className="space-y-6">
        <div className="space-y-2">
          {candidate?.data?.applications?.some(app => (app.application_notes?.length || 0) > 0) ? (
            candidate.data.applications
              .filter(app => (app.application_notes?.length || 0) > 0)
              .map((application, appIndex) => (
                <div key={appIndex} className="rounded-lg">
                  <div className="px-4 pt-4 -mb-2">
                    <h4 className="text-sm font-semibold text-foreground bg-muted px-3 py-1.5 rounded-md inline-block">
                      Notes for: {application.vacancy?.title || "N/A"}
                    </h4>
                  </div>
                  <CandidateNotes
                    applicationId={application.id}
                    notes={application.application_notes || []}
                    hideHeader={true}
                    hideAddButton={true}
                    readOnly={true}
                  />
                </div>
              ))
          ) : (
            <div className="rounded-lg p-4">
              <CandidateNotes
                notes={[]}
                hideHeader={true}
                hideAddButton={true}
                readOnly={true}
              />
            </div>
          )}
        </div>
        </div>
      </DialogBox>

      {/* Booked Slots Dialog */}
      <DialogBoxSkeleton isOpen={showSlotsPopup} onClose={handleCloseSlots}>
        <InterviewDetailsForm
          selectedJob={selectedJob}
          handleCloseSlots={handleCloseSlots}
          candidate={candidate}
          onSuccess={fetchApplications}
        />
      </DialogBoxSkeleton>
    </div>
  );
};

export default CandidateHistory;
